'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Card } from '@app/app/_components/Card/Card';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Input } from '@app/app/_components/Input/Input';
import { Message } from '@app/app/_components/Message/Message';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { InsuredPersonErrors } from '@app/domain/model/eshop/travel/InsuredPersonErrors';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

interface Props {
    age: number;
    name: string;
    surname: string;
    personalCode: string;
    id: string;
    index: number;
    isPrimary: boolean;
    handleNameChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSurnameChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    handlePersonalCodeChange: (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    errors: InsuredPersonErrors;
    ageMismatch?: boolean;
    changeAge: () => void;
}

export function InsuredPersonCard({
    age,
    name,
    surname,
    personalCode,
    id,
    index,
    isPrimary,
    handleNameChange,
    handleSurnameChange,
    handlePersonalCodeChange,
    errors,
    ageMismatch,
    changeAge,
}: Props) {
    const t = useTranslations();

    const personalCodeError = useMemo(() => {
        if (errors.personalCode) {
            return errors.personalCode;
        }
        if (errors.duplicatePersonalCode) {
            return { message: 'eshop.personalAccidents.duplicatePersonalCode' };
        }
        if (ageMismatch) {
            return { message: 'eshop.personalAccidents.wrongCode' };
        }
        return undefined;
    }, [errors.personalCode, errors.duplicatePersonalCode, ageMismatch]);

    const title = isPrimary
        ? t('eshop.personalAccidents.primaryPersonWithAge', { age })
        : t('eshop.personalAccidents.additionalPersonWithAge', { age });

    return (
        <Card className="flex flex-col gap-8 p-6">
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex size-6 items-center justify-center rounded-full bg-primary-400 text-sm text-white">
                            {index + 1}
                        </div>
                        <Heading level="h3">
                            {title}
                            {isPrimary && ` (${t('eshop.personalAccidents.policyholder')})`}
                        </Heading>
                    </div>
                </div>

                <div className="flex flex-col gap-6">
                    <div className="flex gap-6 max-md:flex-col">
                        <Input
                            name={`insuredPersons.${index}.name`}
                            label={t('eshop.personalAccidents.name')}
                            placeholder={t('eshop.personalAccidents.name')}
                            value={name || ''}
                            onChange={handleNameChange(id)}
                            error={errors.name}
                            className="flex-1"
                        />
                        <Input
                            name={`insuredPersons.${index}.surname`}
                            label={t('eshop.personalAccidents.surname')}
                            placeholder={t('eshop.personalAccidents.surname')}
                            value={surname || ''}
                            onChange={handleSurnameChange(id)}
                            error={errors.surname}
                            className="flex-1"
                        />
                        <Input
                            name={`insuredPersons.${index}.personalCode`}
                            label={t('eshop.personalAccidents.personalCode')}
                            placeholder={t('eshop.personalAccidents.personalCode')}
                            value={personalCode || ''}
                            onChange={handlePersonalCodeChange(id)}
                            error={personalCodeError}
                            enableErrorOutline={ageMismatch || errors.duplicatePersonalCode}
                            noSpaces
                            noSymbols
                            className="flex-1"
                        />
                    </div>
                    {ageMismatch && (
                        <Message
                            containerClassName="items-center"
                            addon={
                                <Button
                                    onClick={changeAge}
                                    preventDefault
                                    rightAddon={<ArrowRightIcon className="size-4" />}
                                    size="tiny">
                                    {t('general.change')}
                                </Button>
                            }
                            variant="danger">
                            {t('eshop.personalAccidents.ageMismatchError')}
                        </Message>
                    )}
                </div>
            </div>
        </Card>
    );
}
